/**
 * MessagesPage — /messages
 *
 * A single chat-list screen that works for both the customer side AND the
 * supplier side. The backend already disambiguates the viewer's role and
 * returns the correct contact name + unread count, so this page renders
 * identically for either party.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChatCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

const MessagesPage = () => {
  const navigate = useNavigate();
  const [chats, setChats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      axios
        .get(`${API}/chats`)
        .then((r) => { if (!cancelled) setChats(r.data.chats || []); })
        .catch(() => { if (!cancelled) setChats([]); });
    };
    load();
    const t = setInterval(load, 15000); // soft refresh
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="messages-page">
      <div className="max-w-2xl mx-auto px-5 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-white/60 hover:text-white text-sm mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Back
        </button>

        <h1 className="text-3xl font-black mb-6">Messages</h1>

        {chats === null && (
          <p className="text-white/40 text-sm" data-testid="chats-loading">Loading…</p>
        )}

        {chats && chats.length === 0 && (
          <div className="rounded-2xl p-8 text-center bg-white/[0.02] border border-white/5" data-testid="chats-empty">
            <ChatCircle size={32} weight="fill" className="mx-auto mb-2 text-white/20" />
            <p className="text-white/50 text-sm font-bold mb-1">No conversations yet</p>
            <p className="text-white/30 text-xs">Message a supplier from their profile to start a chat.</p>
          </div>
        )}

        {chats && chats.length > 0 && (
          <ul className="divide-y divide-white/5 rounded-2xl overflow-hidden bg-white/[0.02] border border-white/5">
            {chats.map((c) => (
              <li key={c.chat_id}>
                <button
                  onClick={() => navigate(`/chat/${c.chat_id}`)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition text-left"
                  data-testid={`chat-row-${c.chat_id}`}
                >
                  <div
                    className="w-11 h-11 rounded-full shrink-0 bg-cover bg-center"
                    style={{
                      background: c.contact_avatar
                        ? `url(${c.contact_avatar}) center/cover`
                        : 'linear-gradient(135deg, #B89762, #C29D52)',
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-sm truncate flex-1">{c.contact_name}</p>
                      {c.unread_count > 0 && (
                        <span
                          className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-[#B89762]/40 text-[#0F1014]"
                          data-testid="chat-unread-badge"
                        >
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-white/50 text-xs truncate">
                      {c.last_message || 'No messages yet'}
                    </p>
                  </div>
                  <p className="text-white/30 text-[10px] shrink-0">{timeAgo(c.last_message_at)}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
